import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { ToastService } from './toast.service';
import { ModalService } from './modal.service';
import { AlertService } from './alert.service';
import { removeNullValues } from '@app/core/utils/remove-null-values';
import { logFormErrors } from '@app/core/utils/log-form-errors';


/**
 * A service for handling form submissions, integrating API calls, success/error handling, and user notifications.
 */
@Injectable({
  providedIn: 'root',
})
export class FormApiService {
  /**
   * Creates an instance of `FormApiService` and injects required dependencies.
   *
   * @param toastService - Service to display success or informational messages as toasts.
   * @param modalService - Service to manage modal interactions, such as closing modals.
   * @param alertService - Service to display error messages to the user.
   */
  constructor(
    private readonly toastService: ToastService,
    private readonly modalService: ModalService,
    private readonly alertService: AlertService
  ) {}

  /**
   * Submits a form, making an API call and executing additional options (e.g., notifications, modal handling).
   *
   * @param form - The Angular `FormGroup` representing the form to be submitted.
   * @param apiCall - A function to execute the API call, accepting form data as its argument and returning an observable.
   * @param options - Optional configuration for additional processing, messaging, and callbacks:
   *  - `preprocess`: A function to preprocess form data before submission.
   *  - `successMessage`: A success message to display as a toast upon successful submission.
   *  - `closeModal`: A flag indicating whether to close the modal after a successful submission.
   *  - `callbackParam`: Optional parameter to provide context for closing the modal.
   *
   * @template T - The type of the response expected from the API call.
   *
   * ```typescript
   * this.formApiService.submit(
   *   this.myForm,
   *   formData => this.apiService.saveData(formData),
   *   {
   *     preprocess: (data) => ({...data, additionalField: 'value'}),
   *     successMessage: 'Data saved successfully!',
   *     closeModal: true
   *   }
   * );
   * ```
   */
  public submit<T>(
    form: FormGroup,
    apiCall: (data: any) => Observable<T>,
    options?: {
      preprocess?: (data: any) => any;
      successMessage?: string;
      closeModal?: boolean;
      callbackParam?: any;
    }
  ): void {
    if (!form.valid) {
      form.markAllAsTouched(); // Ensures all fields are marked as touched if validation fails.
      console.error('Form is invalid', form);
      logFormErrors(form);
      return;
    }

    let formData = form.value;
    formData = removeNullValues(formData); // Removes null values from form data.

    // Apply optional preprocessing.
    if (options?.preprocess) {
      formData = options.preprocess(formData);
    }

    // Make the API call with the processed form data.
    apiCall(formData).subscribe({
      next: () => {
        // Close the modal if specified.
        if (options?.closeModal) {
          this.modalService.close(options.callbackParam ?? null);
        }

        // Display success message if specified.
        if (options?.successMessage) {
          this.toastService.show(options.successMessage);
        }
      },
      error: (error) => {
        console.error('API Error', error);
      },
    });
  }
}
